import type React from 'react';
import type { NewInsuredFormValues } from '../types/vida/cadastralMaintenance/newInsuredFormTypes';
import type { PersonFormBaseValues } from '../types/vida/cadastralMaintenance/personFormTypes';
import { formatCepFieldValue } from './cepFieldUtils';
import { formatDateForDateInput } from './inclusionProponentUtil';

export const EMPTY_FORM_VALUES: NewInsuredFormValues = {
    nome: '',
    matricula: '',
    cpf: '',
    sexo: '',
    dataNascimento: '',
    dataCadastro: '',
    cep: '',
    logradouro: '',
    numero: '',
    bairro: '',
    cidade: '',
    uf: '',
    capital: '',
    salario: '',
};

const REQUIRED_FIELDS: Array<keyof NewInsuredFormValues> = [
    'nome',
    'matricula',
    'cpf',
    'sexo',
    'dataNascimento',
    'dataCadastro',
    'cep',
    'logradouro',
    'numero',
    'bairro',
    'cidade',
    'uf',
    'capital',
    'salario',
];

export const getTodayAsFormDate = (): string => {
    const now = new Date();
    const year = String(now.getFullYear());
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const getFormValuesFromInitial = (
    initialValues?: NewInsuredFormValues,
): NewInsuredFormValues => {
    const values = {
        ...EMPTY_FORM_VALUES,
        ...(initialValues ?? {}),
    };

    return {
        ...values,
        dataCadastro: values.dataCadastro.trim() || getTodayAsFormDate(),
    };
};

export const getInputValue = (e: React.FormEvent<HTMLBsdsInputElement>): string => {
    // BsdsInput may provide value through detail.value or target.value depending on platform.
    const target = e.target as HTMLBsdsInputElement;
    const formEvent = e as React.FormEvent<HTMLBsdsInputElement> & {
        detail?: { value?: string };
    };

    return String(formEvent.detail?.value ?? target?.value ?? '');
};

export const BAIRRO_MAX_LENGTH = 30;
export const MATRICULA_MAX_LENGTH = 20;
export const CIDADE_MAX_LENGTH = 40;
export const LOGRADOURO_MAX_LENGTH = 50;
export const COMPLEMENTO_MAX_LENGTH = 30;
export const UF_MAX_LENGTH = 2;
export const CARGO_FUNCAO_MAX_LENGTH = 40;
export const NUMERO_MAX_LENGTH = 6;
/** Alias mantido para compatibilidade com imports existentes. */
export const ADDRESS_TEXT_MAX_LENGTH = LOGRADOURO_MAX_LENGTH;
export const LOGRADOURO_API_MAX_LENGTH = LOGRADOURO_MAX_LENGTH;
export const LOGRADOURO_MAX_LENGTH_BEFORE_NUMERO = 42;
export const MONEY_DIGITS_MAX_LENGTH = 50;
export const PERSON_NAME_MAX_LENGTH = 150;

export const limitPersonNameInput = (value: string): string =>
    value.slice(0, PERSON_NAME_MAX_LENGTH);

export const limitMatriculaInput = (value: string): string =>
    value.slice(0, MATRICULA_MAX_LENGTH);

export const limitBairroInput = (value: string): string =>
    value.slice(0, BAIRRO_MAX_LENGTH);

export const limitCidadeInput = (value: string): string =>
    value.replace(/[^a-zA-ZÀ-ÿ\s'-]/g, '').slice(0, CIDADE_MAX_LENGTH);

export const limitLogradouroInput = (value: string): string =>
    value.slice(0, LOGRADOURO_MAX_LENGTH);

export const limitComplementoInput = (value: string): string =>
    value.slice(0, COMPLEMENTO_MAX_LENGTH);

export const limitUfInput = (value: string): string =>
    value.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, UF_MAX_LENGTH);

export const limitCargoFuncaoInput = (value: string): string =>
    value.slice(0, CARGO_FUNCAO_MAX_LENGTH);

export const limitNumeroInput = (value: string): string =>
    value.replace(/\D/g, '').slice(0, NUMERO_MAX_LENGTH);

export const formatLogradouroWithNumeroForApi = (
    logradouro: string,
    numero: string,
): string => {
    const street = logradouro.trim();
    const number = limitNumeroInput(numero.trim());

    if (!number) {
        return street.slice(0, LOGRADOURO_API_MAX_LENGTH);
    }

    const suffix = `, ${number}`;
    const maxStreetLength = LOGRADOURO_API_MAX_LENGTH - suffix.length;
    const truncatedStreet = street.length > LOGRADOURO_MAX_LENGTH_BEFORE_NUMERO
        ? street.slice(0, LOGRADOURO_MAX_LENGTH_BEFORE_NUMERO)
        : street;

    return `${truncatedStreet.slice(0, maxStreetLength)}${suffix}`.slice(0, LOGRADOURO_API_MAX_LENGTH);
};

export const parseLogradouroFromApi = (endereco: string): { logradouro: string; numero: string } => {
    const trimmed = endereco.trim();
    if (!trimmed) {
        return { logradouro: '', numero: '' };
    }

    const commaIndex = trimmed.lastIndexOf(', ');
    if (commaIndex === -1) {
        return { logradouro: trimmed, numero: '' };
    }

    const possibleNumero = trimmed.slice(commaIndex + 2).trim();
    const possibleLogradouro = trimmed.slice(0, commaIndex).trim();

    if (/^\d{1,6}$/.test(possibleNumero)) {
        return { logradouro: possibleLogradouro, numero: possibleNumero };
    }

    return { logradouro: trimmed, numero: '' };
};

const limitMoneyDigits = (value: string, maxDigits: number): string => {
    let digits = 0;
    let result = '';

    for (const char of value) {
        if (char === ',') {
            if (result.includes(',')) {
                continue;
            }
            result += char;
            continue;
        }

        if (/\d/.test(char)) {
            if (digits >= maxDigits) {
                continue;
            }
            digits += 1;
            result += char;
        }
    }

    return result;
};

export const limitMoneyInput = (value: string): string => {
    const normalized = value.replace(/\./g, ',');
    const sanitized = normalized.replace(/[^\d,]/g, '');
    const commaIndex = sanitized.indexOf(',');

    if (commaIndex === -1) {
        return limitMoneyDigits(sanitized, MONEY_DIGITS_MAX_LENGTH);
    }

    const integerPart = limitMoneyDigits(sanitized.slice(0, commaIndex), MONEY_DIGITS_MAX_LENGTH);
    const digitsUsed = integerPart.replace(/\D/g, '').length;
    const remainingDigits = MONEY_DIGITS_MAX_LENGTH - digitsUsed;
    const decimalPart = remainingDigits > 0
        ? limitMoneyDigits(sanitized.slice(commaIndex + 1).replace(/,/g, ''), remainingDigits)
        : '';

    if (decimalPart) {
        return `${integerPart},${decimalPart}`;
    }

    if (sanitized.endsWith(',')) {
        return `${integerPart},`;
    }

    return integerPart;
};

export const SALARY_DECIMAL_PLACES = 2;
export const SALARY_MAX_DIGITS = MONEY_DIGITS_MAX_LENGTH + SALARY_DECIMAL_PLACES;

export const extractSalaryDigits = (value: string): string =>
    value.replace(/\D/g, '').slice(0, SALARY_MAX_DIGITS);

export const salaryFormValueFromDigits = (digits: string): string => {
    if (!digits) {
        return '';
    }

    const cents = BigInt(digits);
    const integerPart = (cents / 100n).toString();
    const decimalPart = (cents % 100n).toString().padStart(SALARY_DECIMAL_PLACES, '0');

    return `${integerPart},${decimalPart}`;
};

export const normalizeSalaryFormValue = (value: string): string => {
    const digits = extractSalaryDigits(value);
    if (!digits) {
        return '';
    }

    return salaryFormValueFromDigits(digits);
};

export const finalizeSalaryFormValue = (value: string): string =>
    normalizeSalaryFormValue(value);

export const formatSalaryForFormInitial = (value: string | number): string => {
    if (value === '' || value === null || value === undefined) {
        return '';
    }

    if (typeof value === 'number') {
        if (!Number.isFinite(value) || value === 0) {
            return '';
        }

        return value.toFixed(SALARY_DECIMAL_PLACES).replace('.', ',');
    }

    const trimmed = value.trim();
    if (!trimmed) {
        return '';
    }

    return normalizeSalaryFormValue(trimmed);
};

export const formatSalaryForDisplay = (value: string | number): string => {
    const formValue = typeof value === 'number'
        ? formatSalaryForFormInitial(value)
        : (value.includes(',') ? value.trim() : normalizeSalaryFormValue(String(value).trim()));

    if (!formValue) {
        return '';
    }

    const [integerPart, decimalPart = '00'] = formValue.split(',');
    const formattedInteger = Number(integerPart || '0').toLocaleString('pt-BR', {
        maximumFractionDigits: 0,
    });

    return `${formattedInteger},${decimalPart.padEnd(SALARY_DECIMAL_PLACES, '0').slice(0, SALARY_DECIMAL_PLACES)}`;
};

export const formatMoneyForFormDisplay = (value: string | number): string => {
    const trimmed = String(value).trim();
    if (!trimmed) {
        return '';
    }

    return trimmed.replace('.', ',');
};

export const stripMoneyThousandsFromFormValue = (value: string): string => {
    const trimmed = value.trim();
    const commaIndex = trimmed.lastIndexOf(',');

    if (commaIndex === -1) {
        return trimmed.replace(/\./g, '');
    }

    const integerPart = trimmed.slice(0, commaIndex).replace(/\./g, '');
    const decimalPart = trimmed.slice(commaIndex + 1).replace(/[^\d]/g, '');

    return decimalPart ? `${integerPart},${decimalPart}` : integerPart;
};

export const formatMoneyDecimalForApi = (value: string): string =>
    stripMoneyThousandsFromFormValue(value).replace(',', '.');

export const formatSalaryDecimalForApi = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) {
        return '';
    }

    const formValue = normalizeSalaryFormValue(
        trimmed.includes(',') ? stripMoneyThousandsFromFormValue(trimmed) : trimmed,
    );

    if (!formValue) {
        return '';
    }

    return formatMoneyDecimalForApi(formValue);
};

/** Same mask/cents as salary — reused in the Capital field. */
export const normalizeCapitalFormValue = normalizeSalaryFormValue;
export const formatCapitalForDisplay = formatSalaryForDisplay;
export const formatCapitalForFormInitial = formatSalaryForFormInitial;
export const formatCapitalDecimalForApi = formatSalaryDecimalForApi;

export const parseFormMoneyToNumber = (value: string): number => {
    const normalized = formatSalaryDecimalForApi(value);
    if (!normalized) {
        return 0;
    }
    const num = Number(normalized);
    return Number.isFinite(num) ? num : 0;
};

export const isInsuredFormValid = (values: NewInsuredFormValues): boolean =>
    REQUIRED_FIELDS.every((field) => values[field].trim() !== '');

export type PersonFormFieldUpdateResult<T extends PersonFormBaseValues> = {
    partial: Partial<T>;
    shouldFocusCepSufixo?: boolean;
};

export const resolvePersonFormFieldUpdate = <T extends PersonFormBaseValues>(
    field: keyof T,
    value: string,
    _currentValues: T,
): PersonFormFieldUpdateResult<T> => {
    if (field === 'cep') {
        return { partial: { cep: formatCepFieldValue(value) } as Partial<T> };
    }

    if (field === 'nome') {
        return { partial: { nome: limitPersonNameInput(value) } as Partial<T> };
    }

    if (field === 'matricula') {
        return { partial: { matricula: limitMatriculaInput(value) } as Partial<T> };
    }

    if (field === 'cidade') {
        return { partial: { cidade: limitCidadeInput(value) } as Partial<T> };
    }

    if (field === 'bairro') {
        return { partial: { bairro: limitBairroInput(value) } as Partial<T> };
    }

    if (field === 'logradouro') {
        return { partial: { logradouro: limitLogradouroInput(value) } as Partial<T> };
    }

    if (field === 'numero' || field === 'numeroLogradouro') {
        return { partial: { [field]: limitNumeroInput(value) } as Partial<T> };
    }

    if (field === 'complemento') {
        return { partial: { [field]: limitComplementoInput(value) } as Partial<T> };
    }

    if (field === 'uf') {
        return { partial: { uf: limitUfInput(value) } as Partial<T> };
    }

    if (field === 'cargo') {
        return { partial: { [field]: limitCargoFuncaoInput(value) } as Partial<T> };
    }

    if (field === 'salario') {
        return { partial: { salario: normalizeSalaryFormValue(value) } as Partial<T> };
    }

    if (field === 'capital') {
        return { partial: { capital: normalizeSalaryFormValue(value) } as Partial<T> };
    }

    if (field === 'dataNascimento' || field === 'dataCadastro') {
        return { partial: { [field]: formatDateForDateInput(value) } as Partial<T> };
    }

    return { partial: { [field]: value } as Partial<T> };
};
